import { addGifsToChannel, editChannelJson, getChannelGifsById } from '@/app/util/channel-api'
import Button from '@/components/button'
import { GridContext } from '@/components/grid/grid'
import { FlashMessageContext } from '@/context/flash-message'
import UserContext from '@/context/user-context'
import { cn } from '@/util/cn'
import { IChannel, IGif } from '@giphy/js-types'
import { GifOverlayProps, Grid } from '@giphy/react-components'
import { useCallback, useContext, useState } from 'react'
import BulkSelectorOverlay from '../bulk-edit-overlay'
import { ModalContext } from '../modal'
import { useChannelMutate } from './hooks'

type Props = { channelId: IChannel['id']; channel: IChannel }
const GifSelector = ({ channelId, channel }: Props) => {
    const { modal, setModalType } = useContext(ModalContext)
    const { user } = useContext(UserContext)
    const fetchGifs = useCallback(
        async (offset: number) => {
            const data = await getChannelGifsById(user?.channel_id!, { offset, limit: 20 }, document.cookie)
            return data
        },
        [user?.channel_id]
    )
    const { setGifs: setGridGifs } = useContext(GridContext)
    const [gifs, setGifs] = useState<IGif[]>([])
    const [loading, setLoading] = useState(false)
    const { showMessage } = useContext(FlashMessageContext)
    const _BulkSelectorOverlay = useCallback(
        (props: GifOverlayProps) => {
            const index = gifs.indexOf(props.gif)
            const count = index !== -1 ? index + 1 : undefined
            return <BulkSelectorOverlay {...props} count={count} />
        },
        [gifs]
    )
    const mutateChannel = useChannelMutate()
    return modal?.name === 'addGifsToCollection' ? (
        <>
            <div
                className={cn(
                    'relative mt-2 flex flex-col gap-4 overflow-hidden',
                    loading && 'pointer-events-none opacity-80'
                )}
                style={{ height: 'calc(100vh - 100px)' }}
            >
                <Button
                    className={cn(
                        'mx-auto cursor-pointer',
                        gifs.length === 0 && 'pointer-events-none cursor-none bg-giphyCharcoal'
                    )}
                    onClick={async () => {
                        setLoading(true)
                        const result = await addGifsToChannel(
                            gifs.map((g) => g.id as string),
                            channelId
                        )
                        if (result.ok) {
                            showMessage({
                                message: 'Gifs Added',
                                type: 'success',
                            })
                            setGridGifs((g) => [...g, ...gifs])
                            setModalType(null)
                        } else {
                            showMessage({
                                message: 'There was an error',
                                type: 'error',
                            })
                        }
                        if (!channel.featured_gif) {
                            await editChannelJson(channel.id, {
                                id: channel.id,
                                parent: channel.parent,
                                featured_gif: gifs[0].id.toString(),
                            })
                            mutateChannel({ ...channel, featured_gif: gifs[0] })
                        }
                        setLoading(false)
                    }}
                >
                    Add Selected Gifs to Collection
                </Button>
                <div className="animate-fade-in overflow-y-auto">
                    <Grid
                        fetchGifs={fetchGifs}
                        percentWidth="100%"
                        width={1040}
                        columns={4}
                        overlay={_BulkSelectorOverlay}
                        onGifClick={(gif, e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            if (gifs.includes(gif)) {
                                setGifs(gifs.filter((g) => g !== gif))
                            } else {
                                setGifs([...gifs, gif])
                            }
                        }}
                    />
                </div>
            </div>
        </>
    ) : null
}

export default GifSelector
